import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
type Vertical = 'servicios' | 'inmobiliaria' | 'odonto' | 'educacion' | 'ecommerce' | 'estetica';
interface VerticalContent {
  label: string;
  clientMsg: string;
  aiTyping: string;
  aiReply: string;
  panel1Title: string;
  panel1: {
    k: string;
    v: string;
  }[];
  panel2Title: string;
  panel2Chips: string[];
  finalText: string;
  crmText: string;
  followupText: string;
}
const content: Record<Vertical, VerticalContent> = {
  servicios: {
    label: 'Servicios',
    clientMsg: 'Necesito una cotización para mi empresa.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, ¿qué servicio necesitas y para cuántas personas aproximadamente?',
    panel1Title: 'LEAD IDENTIFICADO',
    panel1: [{
      k: 'Servicio',
      v: 'Consultoría'
    }, {
      k: 'Tamaño',
      v: '20-50 personas'
    }, {
      k: 'Intención',
      v: 'Alta'
    }],
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 10:00', 'Mañana · 15:00'],
    finalText: 'REUNIÓN AGENDADA',
    crmText: 'CRM ACTUALIZADO',
    followupText: 'SEGUIMIENTO AUTOMÁTICO ACTIVADO'
  },
  inmobiliaria: {
    label: 'Inmobiliaria',
    clientMsg: 'Hola, vi el apartamento de 3 habitaciones. Quiero visitarlo.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, puedo darte el precio, las características o agendar una visita.',
    panel1Title: 'LEAD IDENTIFICADO',
    panel1: [{
      k: 'Interés',
      v: 'Apto. 3 habitaciones'
    }, {
      k: 'Intención',
      v: 'Alta'
    }, {
      k: 'Estado',
      v: 'Calificado'
    }],
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 11:00', 'Sábado · 10:00'],
    finalText: 'VISITA AGENDADA',
    crmText: 'CRM ACTUALIZADO',
    followupText: 'SEGUIMIENTO AUTOMÁTICO ACTIVADO'
  },
  odonto: {
    label: 'Odontología',
    clientMsg: 'Hola, quiero información sobre diseño de sonrisa.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, puedo ayudarte con información, disponibilidad y agendamiento.',
    panel1Title: 'LEAD IDENTIFICADO',
    panel1: [{
      k: 'Servicio',
      v: 'Diseño de sonrisa'
    }, {
      k: 'Interés',
      v: 'Alto'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 10:00', 'Mañana · 15:00'],
    finalText: 'CITA AGENDADA',
    crmText: 'CRM ACTUALIZADO',
    followupText: 'SEGUIMIENTO AUTOMÁTICO ACTIVADO'
  },
  educacion: {
    label: 'Educación',
    clientMsg: 'Quiero información sobre el programa.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, puedo explicarte el programa, requisitos, duración y agendar una llamada.',
    panel1Title: 'LEAD IDENTIFICADO',
    panel1: [{
      k: 'Programa',
      v: 'Maestría en Marketing'
    }, {
      k: 'Interés',
      v: 'Alto'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 9:00', 'Jueves · 16:00'],
    finalText: 'LLAMADA AGENDADA',
    crmText: 'CRM ACTUALIZADO',
    followupText: 'SEGUIMIENTO AUTOMÁTICO ACTIVADO'
  },
  ecommerce: {
    label: 'E-commerce',
    clientMsg: '¿Tienen disponible este producto?',
    aiTyping: 'Consultando inventario...',
    aiReply: 'Sí, puedo ayudarte con disponibilidad, características y el proceso de compra.',
    panel1Title: 'CLIENTE IDENTIFICADO',
    panel1: [{
      k: 'Producto',
      v: 'Disponible'
    }, {
      k: 'Interés',
      v: 'Compra directa'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    panel2Title: 'PROCESO DE COMPRA',
    panel2Chips: ['Pedido confirmado', 'Envío programado'],
    finalText: 'COMPRA CONFIRMADA',
    crmText: 'CRM ACTUALIZADO',
    followupText: 'SEGUIMIENTO POSTVENTA ACTIVADO'
  },
  estetica: {
    label: 'Estética',
    clientMsg: 'Quiero información sobre depilación láser.',
    aiTyping: 'Analizando intención...',
    aiReply: 'Claro, puedo darte información sobre el servicio y consultar disponibilidad.',
    panel1Title: 'LEAD IDENTIFICADO',
    panel1: [{
      k: 'Servicio',
      v: 'Depilación láser'
    }, {
      k: 'Interés',
      v: 'Alto'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    panel2Title: 'DISPONIBILIDAD ENCONTRADA',
    panel2Chips: ['Mañana · 11:00', 'Viernes · 16:00'],
    finalText: 'CITA AGENDADA',
    crmText: 'CRM ACTUALIZADO',
    followupText: 'SEGUIMIENTO AUTOMÁTICO ACTIVADO'
  }
};
const verticalOrder: Vertical[] = ['servicios', 'inmobiliaria', 'odonto', 'educacion', 'ecommerce', 'estetica'];
const STEP_DELAYS = [200, 1000, 1900, 2900, 4000, 5100, 6200];
export function DemoSection() {
  const [vertical, setVertical] = useState<Vertical>('servicios');
  const [step, setStep] = useState(0);
  const [playKey, setPlayKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, {
    once: true,
    amount: 0.4
  });
  const data = content[vertical];
  useEffect(() => {
    if (!isInView) return;
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setStep(7);
      return;
    }
    const timers = STEP_DELAYS.map((delay, idx) => setTimeout(() => setStep(idx + 1), delay));
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isInView, playKey]);
  const replay = () => {
    setStep(0);
    setPlayKey(k => k + 1);
  };
  const selectVertical = (key: Vertical) => {
    if (key === vertical) return;
    setVertical(key);
    replay();
  };
  return <section id="como-funciona" className="bg-arena py-[72px] md:py-[120px]" data-id="element-62" data-mp="12ttuchoxlclf">
      <div className="max-w-[1000px] mx-auto px-6 md:px-20" data-id="element-63" data-mp="17lk9d0ytcoj6">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="text-center max-w-[560px] mx-auto mb-10" data-id="element-64" data-mp="17n55gv0s0fj5">
          <span className="block font-body text-[11px] uppercase font-medium text-salvia tracking-[0.15em] mb-4" data-id="element-65" data-mp="guo5snu836ak">
            ORIGEN EN ACCIÓN
          </span>
          <h2 className="font-display text-[32px] md:text-[44px] font-bold text-carbon leading-[1.1]" data-id="element-66" data-mp="bx4sbq5cmxjo">
            No te lo contamos. Te lo mostramos.
          </h2>
        </motion.div>

        {/* Vertical toggle */}
        <div className="flex items-center justify-center gap-2 mb-3 flex-wrap px-2 -mx-2 overflow-x-auto" data-id="element-67" data-mp="3u919t7isq1m3">
          {verticalOrder.map(key => <button key={key} onClick={() => selectVertical(key)} className={`font-body text-[13px] font-medium px-4 py-2.5 rounded-[6px] border transition-all duration-200 flex-shrink-0 ${vertical === key ? 'bg-verde text-arena border-verde' : 'bg-transparent text-carbon/60 border-carbon/15 hover:text-carbon hover:border-carbon/30'}`} data-id="element-68" data-mp="3p95679e0gc72">
              {content[key].label}
            </button>)}
        </div>
        <p className="text-center font-body text-[12px] text-carbon/40 italic mb-12 max-w-[480px] mx-auto" data-id="element-69" data-mp="134wltmk07jgk">
          Ejemplos de aplicación — ORIGEN se adapta a distintos negocios con
          procesos de atención, calificación y agendamiento.
        </p>

        {/* Demo card */}
        <div ref={containerRef} className="bg-carbon rounded-[12px] p-6 md:p-10 max-w-[520px] mx-auto" data-id="element-70" data-mp="1zux5j75in9r0">
          <div className="space-y-3 min-h-[340px]" data-id="element-71" data-mp="1q2rd5wbcqqba">
            {/* Client message */}
            <motion.div animate={step >= 1 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex justify-end" data-id="element-72" data-mp="2dslt4mqywuzr">
              <div className="bg-arena text-carbon font-body text-[14px] rounded-[12px] rounded-tr-none px-4 py-2.5 max-w-[85%]" data-id="element-73" data-mp="1tsedcrtezxy7">
                {data.clientMsg}
              </div>
            </motion.div>

            {/* Typing indicator */}
            <motion.div animate={step === 2 ? {
            opacity: 1
          } : {
            opacity: 0
          }} transition={{
            duration: 0.25
          }} className="flex justify-start" data-id="element-74" data-mp="1k3cwkzf1pcj6">
              <div className="bg-verde/20 text-arena/70 font-body text-[12px] italic rounded-[12px] rounded-tl-none px-4 py-2" data-id="element-75" data-mp="2cr0cw29a9h00">
                {data.aiTyping}
              </div>
            </motion.div>

            {/* AI reply */}
            <motion.div animate={step >= 3 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex justify-start -mt-2" data-id="element-76" data-mp="22v5r4y9rj5t0">
              <div className="bg-verde text-arena font-body text-[14px] rounded-[12px] rounded-tl-none px-4 py-2.5 max-w-[85%]" data-id="element-77" data-mp="mfclca84ozyt">
                {data.aiReply}
              </div>
            </motion.div>

            {/* Panel 1 */}
            <motion.div animate={step >= 4 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="bg-arena/5 border border-arena/15 rounded-[12px] px-4 py-3 mt-4" data-id="element-78" data-mp="332kkvd5eurhd">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-azul mb-1.5" data-id="element-79" data-mp="3hqffiz2zb5x3">
                {data.panel1Title} ✓
              </span>
              <div className="font-body text-[13px] text-arena/80 space-y-0.5" data-id="element-80" data-mp="3lqftkim61f3m">
                {data.panel1.map(row => <p key={row.k} data-id="element-81" data-mp="3qpalqb60wg0e">
                    {row.k}: {row.v}
                  </p>)}
              </div>
            </motion.div>

            {/* Panel 2 */}
            <motion.div animate={step >= 5 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="bg-arena/5 border border-arena/15 rounded-[12px] px-4 py-3" data-id="element-82" data-mp="1wex53tagwszq">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-salvia mb-2" data-id="element-83" data-mp="2b7ortk9cba0c">
                {data.panel2Title} ✓
              </span>
              <div className="flex flex-wrap gap-2" data-id="element-84" data-mp="2g9nvywcwmayc">
                {data.panel2Chips.map(chip => <span key={chip} className="font-body text-[12px] text-arena/80 bg-arena/10 rounded-full px-3 py-1" data-id="element-85" data-mp="2k6k0r43rqy7c">
                    {chip}
                  </span>)}
              </div>
            </motion.div>

            {/* Final + CRM */}
            <motion.div animate={step >= 6 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex flex-col gap-1 pt-1" data-id="element-86" data-mp="33xdue21p7i42">
              <span className="font-body text-[14px] font-medium text-arena flex items-center gap-2" data-id="element-87" data-mp="3cwsgkpau16ir">
                {data.finalText} <span className="text-verde" data-id="element-88" data-mp="11bs0sy4mfzd3">✓</span>
              </span>
              <span className="font-body text-[13px] font-medium text-arena/70 flex items-center gap-2" data-id="element-89" data-mp="wauimljm35bl">
                {data.crmText} <span className="text-verde" data-id="element-90" data-mp="sabq0p9w9v8c">✓</span>
              </span>
            </motion.div>

            {/* Follow-up */}
            <motion.div animate={step >= 7 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="font-body text-[13px] font-medium text-arena/55 flex items-center gap-2" data-id="element-91" data-mp="ncimk3zz7fbr">
              {data.followupText} <span className="text-verde" data-id="element-92" data-mp="1b6tmfjzoujax">✓</span>
            </motion.div>
          </div>

          <button onClick={replay} className="mt-6 inline-flex items-center gap-1.5 font-body text-[12px] text-arena/50 hover:text-arena/80 transition-opacity duration-150" data-id="element-93" data-mp="168s79a5dv44q">
            <RotateCcw size={12} data-id="element-94" data-mp="1ywo0u5znofxa" /> Ver de nuevo
          </button>
        </div>
      </div>
    </section>;
}